<script setup>
import {useChatStore} from "@/stores/chat.js";
import {computed, onMounted, onUnmounted, provide, ref} from "vue";
import Contacts from "@/views/chat/Contacts.vue";
import Messages from "@/views/chat/Messages.vue";
import Card from "@/components/Card/index.vue";
import Icon from "@/components/Icon/index.vue";
import Tooltip from "@/components/Tooltip/index.vue";
import {useUserStore} from "@/stores/user.js";
import FiltersModal from "@/views/chat/FiltersModal.vue";
import {useWindowWidth} from "@/composables/useWindowWidth";
import MobileSidebar from "@/views/chat/MobileSidebar.vue";
import Blank from "@/views/chat/Blank.vue";
import {gsap} from "gsap";

const {windowWidth} = useWindowWidth();

const userStore = useUserStore()
const chatStore = useChatStore();
const containerRef = ref(null);
const isResizing = ref(false);
const widthPercentage = ref(20); // Initial width in percentage
const maxWidthPercentage = 30; // Maximum width in percentage
const minWidthPercentage = 20; // Minimum width in percentage
const isShowFiltersModal = computed(() => chatStore.filterDrawer);

const startResize = (event) => {
  isResizing.value = true;
  document.addEventListener("mousemove", handleResize);
  document.addEventListener("mouseup", stopResize);
};

const handleResize = (event) => {
  if (isResizing.value) {
    const parentWidth = containerRef.value.parentElement.clientWidth;
    let newWidth = ((event.clientX - containerRef.value.parentElement.getBoundingClientRect().left) / parentWidth) * 100;

    if (newWidth >= minWidthPercentage && newWidth <= maxWidthPercentage) {
      widthPercentage.value = newWidth;
    }
  }
};

const stopResize = () => {
  isResizing.value = false;
  document.removeEventListener("mousemove", handleResize);
  document.removeEventListener("mouseup", stopResize);
};

function enterMobileSidebar(el) {
  gsap.from(el, {
    x: -100,
    duration: 0.3,
    ease: "power3.out",
  });
}

function leaveMobileSidebar(el) {
  gsap.to(el, {
    x: -100,
    duration: 0.3,
    ease: "power3.out",
  });
}

/*onMounted(() => {
  chatStore.action_get_all_contacts();
})*/
let searchTimeoutId = null
const keyword = ref('')
const isSearching = ref(false)

function onInput(e) {
  //console.log(e.target.value)
  // Cancel any existing search timeout
  clearTimeout(searchTimeoutId)
  // Start a new search timeout
  searchTimeoutId = setTimeout(() => {
    search_by_keyword(e.target.value)
  }, 1000)
}

async function search_by_keyword(query) {
  if (query && query.length > 2) {
    if (query !== keyword.value) {
      isSearching.value = true
      keyword.value = query
      chatStore.searchContactError = ''
      const res = await chatStore.action_searchContact({keyword: query})
      if (res.result === true) {
        //console.log('searched')
      } else {
        //console.log(res)
        chatStore.searchContactError = res
      }
      //We hide the loading with a 1 second delay because we want the search that is performed every two seconds to finish and then hide the loading.
      setTimeout(() => {
        isSearching.value = false;
      }, 1000)
    } else {
      keyword.value = query
    }
  } else {
    keyword.value = query
  }
}

onUnmounted(() => {
  stopResize();
})

function pendingReply() {
  chatStore.filters.pendingReply = !chatStore.filters.pendingReply;
}

const resetSearch = () => {
  chatStore.searchQuery = ''
  chatStore.searchContactError = ''
}

provide('isShowFiltersModal', isShowFiltersModal)
</script>

<template>
  <div class="flex flex-row gap-4 h-full relative">
    <Transition @enter="enterMobileSidebar" @leave="leaveMobileSidebar">
      <mobile-sidebar v-if="chatStore.activeMobileSidebar"/>
    </Transition>
    <Transition name="overlay-fade">
      <div
          v-if="chatStore.activeMobileSidebar"
          class="overlay bg-slate-900 dark:bg-slate-900 dark:bg-opacity-60 bg-opacity-60 backdrop-filter backdrop-blur-sm absolute w-full flex-1 inset-0 z-[9] rounded-md lg:hidden"
          @click="chatStore.activeMobileSidebar = false"
      ></div>
    </Transition>
    <div class="relative select-none hidden lg:block" :style="{ width: widthPercentage + '%' }" ref="containerRef">
      <div
          class="absolute top-0 right-[-10px] h-full w-2 bg-transparent cursor-ew-resize"
          @mousedown="startResize"
      ></div>
      <Card bodyClass="h-full" className="h-full">
        <div class="flex flex-col h-full divide-y divide-slate-100 dark:divide-slate-700">
          <div class="flex gap-3 items-center p-2">
            <div class="flex-1">
              <span class="text-slate-800 dark:text-slate-300 text-sm font-medium flex flex-row gap-3 items-center">
                <span>{{ userStore.getName }}</span>
              </span>
            </div>
            <div class="flex-none flex flex-row gap-2">
              <Tooltip placement="top" arrow>
                <template #button>
                  <div
                      @click="pendingReply"
                      class="h-8 w-8 bg-slate-100 dark:bg-slate-900 dark:text-slate-400 flex flex-col justify-center items-center text-xl rounded-full cursor-pointer"
                      :class="{'border border-blue-500 shadow-[0_0_20px_5px_rgba(59,130,246,0.6)]': chatStore.get_is_pending_reply_active}"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
                      <g fill="currentColor" :class="{'fill-slate-200': chatStore.get_is_pending_reply_active}"
                         fill-rule="evenodd" clip-rule="evenodd">
                        <path
                            d="M5.604 5.45a6.44 6.44 0 0 0-1.883 5.278a.5.5 0 0 1-.994.105a7.44 7.44 0 0 1 2.175-6.096c2.937-2.897 7.675-2.85 10.582.098s2.888 7.685-.05 10.582a7.43 7.43 0 0 1-5.097 2.142a7.5 7.5 0 0 1-2.14-.271a.5.5 0 0 1 .266-.964a6.5 6.5 0 0 0 1.856.235a6.42 6.42 0 0 0 4.413-1.854c2.541-2.506 2.562-6.61.04-9.168s-6.627-2.594-9.168-.088"/>
                        <path
                            d="M3.594 11.363a.5.5 0 0 1-.706.04l-1.72-1.53a.5.5 0 1 1 .664-.746l1.72 1.53a.5.5 0 0 1 .042.706"/>
                        <path
                            d="M2.82 11.3a.5.5 0 0 0 .7.1l2-1.5a.5.5 0 1 0-.6-.8l-2 1.5a.5.5 0 0 0-.1.7M10 6.5a.5.5 0 0 1 .5.5v3.5a.5.5 0 0 1-1 0V7a.5.5 0 0 1 .5-.5"/>
                        <path d="M13.5 10.5a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1 0-1h3a.5.5 0 0 1 .5.5"/>
                      </g>
                    </svg>
                  </div>
                </template>
                <span>Pending Reply</span>
              </Tooltip>
              <Tooltip placement="top" arrow>
                <template #button>
                  <div
                      @click="chatStore.toggleFilterDrawer()"
                      class="h-8 w-8 bg-slate-100 dark:bg-slate-900 dark:text-slate-400 flex flex-col justify-center items-center text-xl rounded-full cursor-pointer"
                      :class="{'border border-blue-500 shadow-[0_0_20px_5px_rgba(59,130,246,0.6)]': chatStore.get_is_filter_active}"
                  >
                    <Icon icon="lets-icons:filter-big" :class="{'text-slate-200': chatStore.get_is_filter_active}"/>
                  </div>
                </template>
                <span>Filter</span>
              </Tooltip>
            </div>
          </div>
          <div class="px-2">
            <div class="search rounded flex items-center gap-3">
              <div class="flex-none text-base text-slate-900 dark:text-slate-400">
                <Icon icon="bytesize:search"/>
              </div>
              <input
                  @input="onInput"
                  v-model="chatStore.searchQuery"
                  placeholder="Search..."
                  class="w-full flex-1 block bg-transparent placeholder:font-normal placeholder:text-slate-400 py-2 focus:ring-0 focus:outline-none dark:text-slate-200 dark:placeholder:text-slate-400"
              />
              <Icon v-if="isSearching" icon="eos-icons:bubble-loading" class="text-white" height="24"/>
              <div v-if="chatStore.searchQuery"
                   class="flex-none text-base text-slate-900 dark:text-slate-400 cursor-pointer"
                   @click="resetSearch">
                <Icon icon="iconamoon:close-bold"/>
              </div>
            </div>
          </div>
          <div class="flex-1 overflow-y-auto custom-scrollbar">
            <Contacts/>
          </div>
        </div>
      </Card>
    </div>
    <div class="flex-1">
      <Card bodyClass="p-0 h-full" class-name="h-full">
        <Messages v-if="chatStore.activeChat"/>
        <Blank v-else/>
      </Card>
    </div>
    <FiltersModal/>
  </div>
</template>

<style scoped lang="scss">
.contact-height {
  height: calc(100% - 99px);
}
</style>
